import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';

// Eager: Home + Login (critical path, always needed)
import HomePage from './pages/Home';
import LoginPage from './pages/admin/Login';

// Lazy-loaded pages
const RegisterPage = lazy(() => import('./pages/admin/Register'));
const DashboardPage = lazy(() => import('./pages/admin/Dashboard'));
const EventDetailPage = lazy(() => import('./pages/admin/EventDetail'));
const PublicEventPage = lazy(() => import('./pages/public/EventPage'));
const PaymentConfirmPage = lazy(() => import('./pages/public/PaymentConfirm'));
const PublicAgendaPage = lazy(() => import('./pages/public/Agenda'));
const DemoPreviewPage = lazy(() => import('./pages/public/DemoPreview'));
const ExhibitorApp = lazy(() => import('./pages/exhibitor/ExhibitorApp'));
const StaffCredentialsPage = lazy(() => import('./pages/staff/Credentials'));
const StaffAccessPage = lazy(() => import('./pages/staff/Access'));
const BadgeDesignerPage = lazy(() => import('./pages/admin/BadgeDesigner'));
const AnalyticsPage = lazy(() => import('./pages/admin/Analytics'));
const WalkInPage = lazy(() => import('./pages/staff/WalkIn'));
const RoomAccessPage = lazy(() => import('./pages/staff/RoomAccess'));
const SuperAdminLogin = lazy(() => import('./pages/superadmin/Login'));
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/Dashboard'));

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB]"></div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }
  
  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

// App Routes
function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      
      {/* Auth Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/register" element={<RegisterPage />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute><ErrorBoundary><DashboardPage /></ErrorBoundary></ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId" element={
        <ProtectedRoute><ErrorBoundary><EventDetailPage /></ErrorBoundary></ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/badges" element={
        <ProtectedRoute><ErrorBoundary><BadgeDesignerPage /></ErrorBoundary></ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/analytics" element={
        <ProtectedRoute><ErrorBoundary><AnalyticsPage /></ErrorBoundary></ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/walkin" element={
        <ProtectedRoute><ErrorBoundary><WalkInPage /></ErrorBoundary></ProtectedRoute>
      } />

      {/* Public Event Routes */}
      <Route path="/evento/:slug" element={<ErrorBoundary><PublicEventPage /></ErrorBoundary>} />
      <Route path="/evento/:slug/agenda" element={<ErrorBoundary><PublicAgendaPage /></ErrorBoundary>} />
      <Route path="/demo/:token" element={<ErrorBoundary><DemoPreviewPage /></ErrorBoundary>} />
      <Route path="/evento/:slug/pago-confirmado" element={<ErrorBoundary><PaymentConfirmPage /></ErrorBoundary>} />
      <Route path="/expositor/:token" element={<ErrorBoundary><ExhibitorApp /></ErrorBoundary>} />

      {/* Staff Routes (No Auth) */}
      <Route path="/staff/:eventId/credenciales" element={<ErrorBoundary><StaffCredentialsPage /></ErrorBoundary>} />
      <Route path="/staff/:eventId/puertas" element={<ErrorBoundary><StaffAccessPage /></ErrorBoundary>} />
      <Route path="/staff/:eventId/salas" element={<ErrorBoundary><RoomAccessPage /></ErrorBoundary>} />
      <Route path="/staff/:eventId/walkin" element={<ErrorBoundary><WalkInPage /></ErrorBoundary>} />

      {/* Fallback */}
      <Route path="/superadmin" element={<ErrorBoundary><SuperAdminLogin /></ErrorBoundary>} />
      <Route path="/superadmin/dashboard" element={<ErrorBoundary><SuperAdminDashboard /></ErrorBoundary>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
